import { useEffect, useState } from "react";
import StaffVerificationIcon from "../../assets/images/StaffVerificationIcon";
import {
  ekycType,
  OnboardConstants,
  pageRouteConstants,
  StaffListConstants,
} from "../../constants/constants";
import NoDetailsForVerifyStaffScreen from "./NoDetailsForVerifyStaffScreen";
import StaffVerifyTile from "./StaffVerifyTile";
import { DLVerificationDetail } from "./DLVerificationDetail";
import Button from "./cta/Button";
import {
  sendSmsButton,
} from "./styles/CommonStyles";
import { BankVerificationDetail } from "./BankVerificationDetail";
import { PANVerificationDetail } from "./PANVerificationDetail";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import {
  fetchUserDetailsAsync,
  resendConsentLinkAsync,
  verifyUserAsync,
} from "../../store/thunks/userThunk";
import PopupModal from "./PopupModal";
import {
  resetResendConsentSMSStatus,
  resetUpdateUserStatus,
  resetUserVerificationStatus,
  STATUS,
} from "../../store/slices/userDetailslice";
import LoaderPopupModel from "./LoaderPopup";
import { useLocation, useNavigate } from "react-router-dom";
import ConsentLinkIcon from "../../assets/images/ConsentLinkIcon";
import {
  readFromLocalStorage,
  writeToLocalStorage,
} from "../../utils/DataEncryption";
import SendSMSIcon from "../../assets/images/SendSMSIcon";
import InfoIcon from "../../assets/images/InfoIcon";

export interface PopupMessage {
  title: string;
  message: string;
}

const componentMap: Record<string, React.ComponentType<any>> = {
  [OnboardConstants.DrivingLicense]: DLVerificationDetail,
  [OnboardConstants.BankDetails]: BankVerificationDetail,
  [OnboardConstants.PanCard]: PANVerificationDetail,
};
const quickLinksData = [
  { title: OnboardConstants.DrivingLicense },
  { title: OnboardConstants.BankDetails },
  { title: OnboardConstants.PanCard },
];

export const SideMenuStaffVerification = () => {
  const userDetails = useSelector((state: RootState) => state.userDetails.user);
  const location = useLocation();
  const [userInfo, setUserInfo] = useState(userDetails);
  const [activeTile, setActiveTile] = useState<string | null>(null);
  const [showNoDetailComponent, setShowNoDetailComponent] =
    useState<boolean>(false);
  const [showDetailComponent, setShowDetailComponent] =
    useState<boolean>(false);
  const [showCancelAndSaveComponent, setShowCancelAndSaveComponent] =
    useState<boolean>(false);
  const [showPopUp, setShowPopUp] = useState<boolean>(false);
  const [popUpMsg, setPopUpMsg] = useState<PopupMessage>({
    title: "",
    message: "",
  });
  const [verifyTitle, setVerifyTitle] = useState("");
  const [showVerifyButton, setShowVerifyButton] = useState<boolean>(false);
  const dispatch = useDispatch<AppDispatch>();
  const userVerificationInfo = useSelector(
    (state: RootState) => state.userDetails.userVerification
  );
  const userVerificationStatus = useSelector(
    (state: RootState) => state.userDetails.userVerificationStatus
  );
  const userVerificationErrorMessage = useSelector(
    (state: RootState) => state.userDetails.userVerificationErrorMsg
  );
  const updateUserLoading = useSelector(
    (state: RootState) => state.userDetails.updateUserLoading
  );
  const [isVerifyStaffLoading, setIsVerifyStaffLoading] = useState(false);
  const navigate = useNavigate();

  const SelectedVerificationComponent = componentMap[activeTile ?? ""];
  const resendConsentSMSErrorMsg = useSelector(
    (state: RootState) => state.userDetails.resendConsentnErrorMsg
  );
  const resendConsentSMSLoading = useSelector(
    (state: RootState) => state.userDetails.resendConsentMessageStatus
  );
  const resendConsentSMS = useSelector(
    (state: RootState) => state.userDetails.resendConsentMessage
  );

  useEffect(() => {
    setUserInfo(userDetails);
  }, [userDetails]);

  useEffect(() => {
    if (location.pathname === pageRouteConstants.onboardRoute) {
      if (userInfo?.id) {
        writeToLocalStorage(
          "userIdForStaffVerification",
          userInfo?.id.toString() ?? ""
        );
      }
      dispatch(
        fetchUserDetailsAsync(
          readFromLocalStorage("userIdForStaffVerification") ?? ""
        )
      );
    }
  }, []);

  useEffect(() => {
    if (userVerificationStatus === STATUS.SUCCESS) {
      if (userVerificationInfo?.verified === true) {
        setPopUpMsg({
          title: OnboardConstants.verifyStaffPopupTitle,
          message:`The employee ${userVerificationInfo.userNameFromEKyc} has been successfully verified with the details of their ${activeTile}`,
        });
        setShowPopUp(true);
      } else {
        setPopUpMsg({
          title: OnboardConstants.verifyStaffFailedPopupTitle,
          message: userVerificationInfo?.comments ?? "Failed to verify staff",
        });
        setShowPopUp(true);
      }
    }
    if (userVerificationStatus === STATUS.FAILED) {
      setPopUpMsg({
        title: OnboardConstants.verifyStaffFailedPopupTitle,
        message: userVerificationErrorMessage ?? "Failed to verify staff",
      });
      setShowPopUp(true);
    }
  }, [userVerificationInfo, userVerificationStatus]);

  useEffect(() => {
    if (resendConsentSMSLoading === STATUS.SUCCESS) {
      if (resendConsentSMS?.smsSent === true) {
        setPopUpMsg({
          title: OnboardConstants.resendConsentSMS,
          message: `${OnboardConstants.resendSMSSuccessMessage}`,
        });
        setShowPopUp(true);
      } else {
        setPopUpMsg({
          title: StaffListConstants.APIErrorMessage,
          message: resendConsentSMS?.message ?? "Failed to send consent SMS",
        });
        setShowPopUp(true);
      }
    }
    if (resendConsentSMSLoading === STATUS.FAILED) {
      setPopUpMsg({
        title: StaffListConstants.APIErrorMessage,
        message: resendConsentSMSErrorMsg ?? "Failed to send consent SMS",
      });
      setShowPopUp(true);
    }
  }, [resendConsentSMSLoading, resendConsentSMS]);

  useEffect(() => {
    if (
      userVerificationStatus == STATUS.LOADING ||
      resendConsentSMSLoading == STATUS.LOADING
    )
      setIsVerifyStaffLoading(true);
    else setIsVerifyStaffLoading(false);
  }, [userVerificationStatus, updateUserLoading, resendConsentSMSLoading]);

  useEffect(() => {
    checkDetailsExists();
  }, [activeTile]);

  const getRequestObj = () => {
    let data: any = {
      userId: userInfo?.id,
    };
    switch (activeTile) {
      case OnboardConstants.DrivingLicense:
        data.dateOfBirth = userInfo?.dateOfBirth;
        data.drivingLicenseNumber = userInfo?.professionalDetails?.drivingLicenceNumber;
        data.ekycType = ekycType.drivingLisense;
        break;
      case OnboardConstants.BankDetails:
        data.accountNumber = userInfo?.bankDetails?.accountNumber;
        data.ifscCode = userInfo?.bankDetails.ifscCode;
        data.ekycType = ekycType.bank;
        break;
      case OnboardConstants.PanCard:
        data.panNumber = userInfo?.bankDetails.panNumber;
        data.ekycType = ekycType.pan;
        break;
    }

    return data;
  };

  const verifyStaff = async () => {
    console.log(getRequestObj());
    await dispatch(verifyUserAsync(getRequestObj()));
  };
  const handleVerifyButtonChange = async (title: string) => {
    if (activeTile && activeTile === title && checkDetailsExists()) {
      await verifyStaff();
    }
  };
  const handleSelectedTileChange = (title: string) => {
    console.log("object");
    setShowCancelAndSaveComponent(false);
    setShowNoDetailComponent(false);
    setShowDetailComponent(false);
    if (activeTile === title) {
      checkDetailsExists();
    }
    setActiveTile(title);
  };
  const checkDetailsExists = () => {
    switch (activeTile) {
      case OnboardConstants.DrivingLicense:
        if (
          userInfo?.professionalDetails?.drivingLicenceNumber &&
          userInfo?.professionalDetails?.drivingLicenceNumber.length !== 0 &&
          userInfo?.dateOfBirth?.length !== 0
        ) {
          setShowNoDetailComponent(false);
          setShowDetailComponent(true);
          return true;
        } else {
          setShowDetailComponent(false);
          setShowNoDetailComponent(true);
          return false;
        }
      case OnboardConstants.BankDetails:
        if (
          userInfo?.bankDetails?.accountNumber &&
          userInfo?.bankDetails?.accountNumber?.length !== 0 &&
          userInfo?.bankDetails?.ifscCode &&
          userInfo?.bankDetails?.ifscCode?.length !== 0
        ) {
          setShowNoDetailComponent(false);
          setShowDetailComponent(true);
          return true;
        } else {
          setShowDetailComponent(false);
          setShowNoDetailComponent(true);
          return false;
        }
      case OnboardConstants.PanCard:
        if (
          userInfo?.bankDetails?.panNumber &&
          userInfo?.bankDetails?.panNumber?.length !== 0
        ) {
          setShowNoDetailComponent(false);
          setShowDetailComponent(true);
          return true;
        } else {
          setShowDetailComponent(false);
          setShowNoDetailComponent(true);
          return false;
        }
    }
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    let { name, value }: any = e.target;
    switch (activeTile) {
      case OnboardConstants.DrivingLicense:
        if (name === "dateOfBirth") {
          let dateValue = new Date(value);
          if (!isNaN(dateValue.getTime())) {
            // If the value is data converting it to yyyy/mm//dd format
            value = value.split("-").join("/");
          }
          setUserInfo((prevData: any) => ({
            ...prevData,
            [name]: value,
          }));
        } else {
          setUserInfo((prevData: any) => ({
            ...prevData,
            professionalDetails: {
              ...prevData.professionalDetails,
              [name]: value,
            },
          }));
        }
        break;
      case OnboardConstants.BankDetails:
      case OnboardConstants.PanCard:
        setUserInfo((prevData: any) => ({
          ...prevData,
          bankDetails: {
            ...prevData.bankDetails,
            [name]: value,
          },
        }));
    }
  };
  const handleAddDetail = () => {
    setShowCancelAndSaveComponent(true);
    setShowNoDetailComponent(false);
    setShowDetailComponent(true);
  };

  const handleResendConsentButtonChange = () => {
    dispatch(resendConsentLinkAsync(userInfo?.id.toString() ?? ""));
  };

  const closeModal = () => {
    setShowPopUp(false);
    dispatch(resetUserVerificationStatus());
    dispatch(resetResendConsentSMSStatus());
    setPopUpMsg({
      title: "",
      message: "",
    });
    dispatch(fetchUserDetailsAsync(userInfo?.id.toString() ?? ""));
  };

  const handleDoneButtonChange = () => {
    sessionStorage.removeItem("userIdForStaffVerification");
    dispatch(resetUserVerificationStatus());
    dispatch(resetResendConsentSMSStatus());
    navigate(`/dashboard`);
  };

  const checkStaffVerificationStatus = () => {
    if (
      userInfo?.ekycStatus?.drivingLicense === true ||
      userInfo?.ekycStatus?.bank === true ||
      userInfo?.ekycStatus?.pan === true
    )
      return true;
    else return false;
  };

  const checkIsVerified = (tileName: string) => {
    switch (tileName) {
      case OnboardConstants.DrivingLicense:
        if (userInfo?.ekycStatus?.drivingLicense === true) return true;
        else return false;
      case OnboardConstants.BankDetails:
        if (userInfo?.ekycStatus?.bank === true) return true;
        else return false;
      case OnboardConstants.PanCard:
        if (userInfo?.ekycStatus?.pan === true) return true;
        else return false;
    }
    return false;
  };
  const handelCancelButton = () =>{
    setActiveTile(null);
  }
  return (
    <div>
      <div className="h-[680px] overflow-y-auto ">
        {isVerifyStaffLoading && (
          <LoaderPopupModel isOpen={isVerifyStaffLoading} />
        )}
        <div className="md:ml-4">
          <div className="flex  justify-between mb-3 ml-2 md:ml-6">
            <div className="flex items-center">
              <div className=" ">
                <StaffVerificationIcon />
              </div>
              <div
                className=" ml-1 md:ml-3 text-loginSubTitle consentRefreshDescription2 md:consentSubmitButtonont-poppins"
              >
                {OnboardConstants.StaffVerification}
              </div>
            </div>
            <div className=" flex pr-2 md:pr-6">
              {userInfo?.consentStatus === false &&
                location.pathname === pageRouteConstants.onboardRoute && (
                  <Button
                    handleClick={handleResendConsentButtonChange}
                    suffixIcon={<SendSMSIcon />}
                    buttonText={StaffListConstants.sendConsentSMSLink}
                    className={`${sendSmsButton} font-nunito  px-3 md:px-10 py-1  `}
                  />
                 )} 

              <Button
                handleClick={
                  checkStaffVerificationStatus()
                    ? handleDoneButtonChange
                    : () => {}
                }
                buttonText={OnboardConstants.Done}
                className={`focus:ring-4 focus:ring-gray-300 ml-2 md:ml-4 py-2 md:py-3 px-4 md:px-8 rounded-md transition duration-300  ${
                  checkStaffVerificationStatus()
                    ? "bg-primary  text-white "
                    : "bg-sideMenuHelpBox text-popUpTitleColor"
                } `}
              />
            </div>
          </div>
          <div className="border-t  border-formBorder ml-6 mr-6"></div>

          {userInfo?.consentStatus === true && (
            <div className="ml-3 md:ml-6 mr-3 md:mr-6">
              <div className="bg-consentMessageColor px-4 flex items-center py-4 mt-8">
                <div className="h-4 w-4 ">
                  <InfoIcon />
                </div>
                <div className="quickLinksDescription text-inputTextColor pl-4">
                  {OnboardConstants.ekycDescription}
                </div>
              </div>
            </div>
          )}
          <div className=" flex  ">
            <div className="flex flex-wrap w-full mr-6 md:justify-around">
              {Object.entries(quickLinksData).map(([key, value]) => (
                <div
                  key={value.title}
                  className={`cursor-pointer md:w-1/3 w-1/2
                `}
                >
                  <StaffVerifyTile
                    title={value.title}
                    showVerify={
                      userInfo?.consentStatus &&
                      userInfo?.consentStatus === true
                        ? true
                        : false
                    }
                    handleVerifyButtonChange={handleVerifyButtonChange}
                    handleSelectedTileChange={handleSelectedTileChange}
                    backgroundColor={
                      activeTile === value.title ? "bg-sideMenubackground" : ""
                    }
                    activeTile={activeTile ?? ""}
                    isVerifed={checkIsVerified(value.title)}
                    userInfo={userInfo}
                    setVerifyTitle={setVerifyTitle}
                    setShowVerifyButton={setShowVerifyButton}
                    showOr={Number(key) !== 0}
                  />
                </div> 
              ))}
            </div>
          </div>
        </div>
        {userInfo?.consentStatus === false && (
          <div className="ml-3 md:ml-6 mr-3 md:mr-6">
            <div className="bg-consentLinkBackgroundColor px-2 md:px-4 ml-2 md:ml-4 flex items-center py-2 md:py-4 mt-8">
              <ConsentLinkIcon />
              <div className="quickLinksDescription text-inputTextColor pl-4">
                {OnboardConstants.ConsentLinkDescription}
              </div>
            </div>
          </div>
        )}
        {activeTile && (
          <div className=" flex justify-between mt-6 pb-2 ml-8 border-b">
            <div className="staffVerifyTitle text-inputTextColor  mt-8 ">{`${activeTile} ${OnboardConstants.details}`}</div>
           
          </div>
        )}
        {showNoDetailComponent && (
          <div className="pt-12">
            <NoDetailsForVerifyStaffScreen
              title={activeTile}
              handleAddDetail={handleAddDetail}
            />
          </div>
        )}
        {showDetailComponent && SelectedVerificationComponent && (
          <div>
            <SelectedVerificationComponent
              userInfo={userInfo}
              handleChange={handleChange}
              isDetailPresent={!showCancelAndSaveComponent}
              verifyTitle={verifyTitle}
              showVerifyButton={showVerifyButton}
              handleVerifyButtonChange={handleVerifyButtonChange}
              handelCancelButton={handelCancelButton}
            />
          </div>
        )}
        {showPopUp && (
          <PopupModal
            popUpTitle={popUpMsg.title}
            popUpMsg={popUpMsg.message}
            buttonText={StaffListConstants.Okay}
            isOpen={showPopUp}
            onClose={closeModal}
            isSucessMessage={
              userVerificationInfo?.verified === true ||
              resendConsentSMS?.smsSent === true
                ? true
                : false
            }
          />
        )}
      </div>
    </div>
  );
};
